import { LBBContactPhoneNumber, LBBContactPhoneNumberKind } from 'sb-lib/types';
import { IconName } from 'shared/components/Icon';
import {
  CallDisposition,
  CallTask,
  CallTaskStatus,
  ContactList,
  DialSession,
  DialTask,
  SourceType,
} from 'types/outbound';

export function getTaskPendingNumbers(t: CallTask | DialTask): LBBContactPhoneNumber[] {
  const badNumbers = new Set([
    ...(t.invalidNumbers ?? []),
    ...(t.attemptedNumbers ?? []),
  ]);
  const phoneNumbers = t.contact?.phoneNumbers ?? [];
  return phoneNumbers.filter(p => !badNumbers.has(p.number));
}

export function getPhoneNumberIcon(p: LBBContactPhoneNumber): IconName {
  switch (p.kind) {
    case LBBContactPhoneNumberKind.MOBILE:
      return 'mobile';
    case LBBContactPhoneNumberKind.HOME:
      return 'houseOutline';
    case LBBContactPhoneNumberKind.WORK:
      return 'company2';
    case LBBContactPhoneNumberKind.FAX:
      return 'fax';
    default:
      return 'phoneOutline';
  }
}

export const getActivityIcon = (activity: {
  dialTask: null | {
    sourceType: SourceType;
  };
}):
  | 'apollo'
  | 'dialer'
  | 'hubspot'
  | 'outreach'
  | 'question'
  | 'salesforce'
  | 'salesloft'
  | 'servicebell' => {
  if (!activity.dialTask) {
    return 'question';
  }

  switch (activity.dialTask.sourceType.toLowerCase()) {
    case SourceType.APOLLO_SEQUENCE:
    case SourceType.APOLLO_TASK:
      return 'apollo';
    case SourceType.CONTACT:
    case SourceType.DIALPAD:
      return 'dialer';
    case SourceType.HUBSPOT_LIST:
    case SourceType.HUBSPOT_TASK:
      return 'hubspot';
    case SourceType.OUTREACH_SEQUENCE:
    case SourceType.OUTREACH_TASK:
      return 'outreach';
    case SourceType.SALESFORCE_REPORT:
      return 'salesforce';
    case SourceType.SALESLOFT_CADENCE:
    case SourceType.SALESLOFT_TASK:
      return 'salesloft';
    case SourceType.SERVICEBELL_LIST:
      return 'servicebell';
    default:
      return 'question';
  }
};

export const getActivityCallDisposition = (
  activity: {
    externalServiceDispositionId?: string;
    dialTask: null | {
      sourceType: SourceType;
      status: CallTaskStatus;
    };
  },
  apolloCallDispositions: CallDisposition[],
  hubspotCallDispositions: CallDisposition[],
  outreachCallDispositions: CallDisposition[],
  salesforceCallDispositions: CallDisposition[],
  salesloftCallDispositions: CallDisposition[],
  servicebellCallDispositions: CallDisposition[],
): string => {
  if (!activity.externalServiceDispositionId) {
    return activity.dialTask?.status ?? '';
  }

  const res = (() => {
    switch (activity.dialTask?.sourceType.toLowerCase()) {
      case SourceType.APOLLO_SEQUENCE:
      case SourceType.APOLLO_TASK:
        return apolloCallDispositions.find(
          d => d.id === activity.externalServiceDispositionId,
        )?.label;
      case SourceType.HUBSPOT_LIST:
      case SourceType.HUBSPOT_TASK:
        return hubspotCallDispositions.find(
          d => d.id === activity.externalServiceDispositionId,
        )?.label;
      case SourceType.OUTREACH_SEQUENCE:
      case SourceType.OUTREACH_TASK:
        return outreachCallDispositions.find(
          d => d.id === activity.externalServiceDispositionId,
        )?.label;
      case SourceType.SALESFORCE_REPORT:
        return salesforceCallDispositions.find(
          d => d.id === activity.externalServiceDispositionId,
        )?.label;
      case SourceType.SALESLOFT_CADENCE:
      case SourceType.SALESLOFT_TASK:
        return salesloftCallDispositions.find(
          d => d.id === activity.externalServiceDispositionId,
        )?.label;
      case SourceType.SERVICEBELL_LIST:
        return servicebellCallDispositions.find(
          d => d.id === activity.externalServiceDispositionId,
        )?.label;
      default:
        return activity.dialTask?.status ?? '';
    }
  })();

  return res as CallTaskStatus;
};

export const getActivityContactList = (
  activity: {
    dialTask: null | {
      session?: DialSession;
      sourceId?: string | number | null;
      sourceType: SourceType;
    };
  },
  apolloContactLists: ContactList[],
  hubspotContactLists: ContactList[],
  outreachContactLists: ContactList[],
  salesforceContactLists: ContactList[],
  salesloftContactLists: ContactList[],
  servicebellContactLists: ContactList[],
): string | undefined => {
  const sourceId = activity.dialTask?.session?.sourceId ?? activity.dialTask?.sourceId;
  switch (activity.dialTask?.sourceType.toLowerCase()) {
    case SourceType.APOLLO_SEQUENCE:
      return apolloContactLists.find(list => list.id === sourceId)?.name;
    case SourceType.APOLLO_TASK:
      return 'One-off Apollo Task';
    case SourceType.CONTACT:
    case SourceType.DIALPAD:
      return 'Dialpad';
    case SourceType.HUBSPOT_LIST:
      return hubspotContactLists.find(list => list.id === sourceId)?.name;
    case SourceType.HUBSPOT_TASK:
      return 'One-off HubSpot Task';
    case SourceType.OUTREACH_SEQUENCE:
      return outreachContactLists.find(list => list.id === sourceId)?.name;
    case SourceType.OUTREACH_TASK:
      return 'One-off Outreach Task';
    case SourceType.SALESFORCE_REPORT:
      return salesforceContactLists.find(list => list.id === sourceId)?.name;
    case SourceType.SALESLOFT_CADENCE:
      return salesloftContactLists.find(list => list.id === sourceId)?.name;
    case SourceType.SALESLOFT_TASK:
      return 'One-off Salesloft Task';
    case SourceType.SERVICEBELL_LIST:
      return servicebellContactLists.find(list => list.id === sourceId)?.name;
    default:
      return undefined;
  }
};

export const getSourceTypeTooltip = (sourceType: SourceType): string => {
  switch (sourceType) {
    case SourceType.APOLLO_SEQUENCE:
      return 'Apollo Sequence';
    case SourceType.APOLLO_TASK:
      return 'Apollo Task';
    case SourceType.CONTACT:
    case SourceType.DIALPAD:
      return 'Dialpad';
    case SourceType.HUBSPOT_LIST:
      return 'HubSpot List';
    case SourceType.HUBSPOT_TASK:
      return 'HubSpot Task';
    case SourceType.OUTREACH_SEQUENCE:
      return 'Outreach List';
    case SourceType.OUTREACH_TASK:
      return 'Outreach Task';
    case SourceType.SALESFORCE_REPORT:
      return 'Salesforce Report';
    case SourceType.SALESLOFT_CADENCE:
      return 'Salesloft Cadence';
    case SourceType.SALESLOFT_TASK:
      return 'Salesloft Task';
    case SourceType.SERVICEBELL_LIST:
      return 'ServiceBell List';
    default:
      return 'Unknown';
  }
};

const friendlyNames: Record<CallTaskStatus, string> = {
  [CallTaskStatus.CANCELED]: 'Canceled',
  [CallTaskStatus.FAILED]: 'Failed',
  [CallTaskStatus.NO_NUMBER]: 'No Number',
  [CallTaskStatus.ERROR]: 'Error',
  [CallTaskStatus.BAD_NUMBER]: 'Bad Number',
  [CallTaskStatus.RETRY]: 'Dialing Next Number',
  [CallTaskStatus.LEFT_VOICEMAIL]: 'Left Voicemail',
  [CallTaskStatus.BUSY]: 'Line Busy',
  [CallTaskStatus.PENDING]: 'Pending',
  [CallTaskStatus.INITIATED]: 'Dialing',
  [CallTaskStatus.DIALING]: 'Dialing',
  [CallTaskStatus.RINGING]: 'Ringing',
  [CallTaskStatus.IN_PROGRESS]: 'Ringing',
  [CallTaskStatus.ANSWERED]: 'Answered',
  [CallTaskStatus.NO_ANSWER]: 'No Answer',
  [CallTaskStatus.COMPLETED]: 'Completed',
  [CallTaskStatus.SKIPPED]: 'Skip',
};

export const getCallStatusFriendlyName = (status: CallTaskStatus) => {
  return friendlyNames[status];
};
